import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Settings } from 'lucide-react';
import { ASSETS } from '../../assetManifest';
import { CONSTANTS } from '../../data';
import { AudioManager } from '../../lib/AudioManager';
import SettingsModal from './SettingsModal';
import Card from '../game/Card';

const TitleScreen = ({ onSelectMode, audioManager }: { onSelectMode: (mode: 'hotseat' | 'vs_cpu') => void; audioManager: AudioManager }) => {
  const [showSettings, setShowSettings] = useState(false);

  // Generate background cards with random properties
  const backgroundCards = useMemo(() => {
    const cards = [];
    const cardDistribution = CONSTANTS.CARD_DISTRIBUTION;
    const config = CONSTANTS.BACKGROUND_CARDS;
    
    for (let i = 0; i < config.COUNT; i++) {
      const randomCard = cardDistribution[Math.floor(Math.random() * cardDistribution.length)];
      const opacity = config.OPACITY_RANGE[0] + Math.random() * (config.OPACITY_RANGE[1] - config.OPACITY_RANGE[0]);
      const scale = config.SCALE_RANGE[0] + Math.random() * (config.SCALE_RANGE[1] - config.SCALE_RANGE[0]);
      const blur = config.BLUR_RANGE[0] + Math.random() * (config.BLUR_RANGE[1] - config.BLUR_RANGE[0]);
      const rotation = config.ROTATION_RANGE[0] + Math.random() * (config.ROTATION_RANGE[1] - config.ROTATION_RANGE[0]);
      
      cards.push({
        id: `bg-card-${i}`,
        card: {
          id: `bg-${i}`,
          color: randomCard.color,
          number: randomCard.number,
          imageAlias: randomCard.imageAlias
        },
        style: {
          position: 'absolute' as const,
          left: `${Math.random() * 100}%`,
          top: `${Math.random() * 100}%`,
          filter: `blur(${blur}px)`,
          zIndex: 1,
          pointerEvents: 'none' as const,
        },
        animationProps: {
          opacity,
          scale,
          rotation
        },
        animationDelay: Math.random() * 10
      });
    }
    
    return cards;
  }, []);

  const titleCards = useMemo(() => {
    const cards = [];
    const cardDistribution = CONSTANTS.CARD_DISTRIBUTION;
    const usedNumbers = new Set();
    while (cards.length < 3) {
      const randomCardInfo = cardDistribution[Math.floor(Math.random() * cardDistribution.length)];
      if (!usedNumbers.has(randomCardInfo.number)) {
        cards.push({
          id: `title-card-${cards.length}`,
          color: randomCardInfo.color,
          number: randomCardInfo.number,
          imageAlias: randomCardInfo.imageAlias,
        });
        usedNumbers.add(randomCardInfo.number);
      }
    }
    return cards;
  }, []);

  const handleSelectMode = (mode: 'hotseat' | 'vs_cpu') => {
    audioManager.playSound('buttonClick');
    onSelectMode(mode);
  };

  const handleSettingsClick = () => {
    audioManager.playSound('buttonClick');
    setShowSettings(true);
  };

  const handleSettingsClose = () => {
    audioManager.playSound('buttonClick');
    setShowSettings(false);
  };

  return (
    <motion.div
      className="fixed inset-0 z-[4000] flex flex-col items-center justify-center"
      style={{
        background: `linear-gradient(135deg, var(--color-primary), var(--color-primary-light), var(--color-background))`
      }}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.7 }}
    >
      {/* Decorative background elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div
          className="absolute top-10 left-10 w-32 h-32 rounded-full blur-xl"
          style={{ background: 'var(--color-background-light)', opacity: 0.15 }}
        ></div>
        <div
          className="absolute top-1/3 right-20 w-48 h-48 rounded-full blur-2xl"
          style={{ background: 'var(--color-accent)', opacity: 0.10 }}
        ></div>
        <div
          className="absolute bottom-20 left-1/4 w-40 h-40 rounded-full blur-xl"
          style={{ background: 'var(--color-secondary)', opacity: 0.10 }}
        ></div>
        <div
          className="absolute bottom-10 right-10 w-24 h-24 rounded-full blur-lg"
          style={{ background: 'var(--color-background-light)', opacity: 0.10 }}
        ></div>
      </div>

      {/* Background Cards */}
      <div className="absolute inset-0 overflow-hidden">
        {backgroundCards.map((bgCard) => (
          <motion.div
            key={bgCard.id}
            style={bgCard.style}
            initial={{ 
              opacity: 0, 
              scale: 0.5,
              rotate: 0
            }}
            animate={{ 
              opacity: bgCard.animationProps.opacity,
              scale: bgCard.animationProps.scale,
              rotate: bgCard.animationProps.rotation,
              y: [0, -20, 0]
            }}
            transition={{ 
              duration: 2,
              delay: bgCard.animationDelay,
              y: {
                duration: 8 + Math.random() * 4,
                repeat: Infinity,
                ease: "easeInOut"
              }
            }}
          >
            <Card card={bgCard.card} />
          </motion.div>
        ))}
      </div>

      {/* Settings Button */}
      <motion.div
        className="absolute top-6 right-6 z-[100]"
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ delay: 1.0 }}
      >
        <motion.button
          onClick={handleSettingsClick}
          className="flex items-center justify-center w-14 h-14 rounded-full font-bold text-base transition-all duration-300 shadow-lg border-2"
          style={{
            background: `linear-gradient(to right, var(--color-primary-dark), var(--color-primary))`,
            color: 'var(--color-text)',
            borderColor: 'var(--color-accent)',
            boxShadow: `0 10px 25px rgba(0, 0, 0, 0.3)`
          }}
          whileHover={{ scale: 1.05, y: -2 }}
          whileTap={{ scale: 0.95 }}
        >
          <Settings size={24} />
        </motion.button>
      </motion.div>

      <div className="flex flex-col items-center gap-8 relative z-10">
        {/* 3 random cards as decorative element */}
        <motion.div
          className="flex justify-center items-center mb-2"
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ delay: 0.2, type: "spring", stiffness: 120, damping: 10 }}
        >
          <div className="flex">
            {titleCards.map((card, index) => (
              <motion.div
                key={card.id}
                className="origin-bottom"
                style={{
                  marginLeft: index > 0 ? '-4rem' : '0', // Overlap cards
                  zIndex: index,
                }}
                animate={{
                  rotate: (index - 1) * 15, // -15, 0, 15 degrees
                  y: index === 1 ? -20 : 0, // Middle card is higher
                }}
                whileHover={{
                  y: -30,
                  zIndex: 10,
                  rotate: 0,
                  scale: 1.05,
                }}
                transition={{ type: "spring", stiffness: 300 }}
              >
                <Card card={card} size="lg" />
              </motion.div>
            ))}
          </div>
        </motion.div>
        <motion.h1
          className="text-6xl md:text-7xl font-extrabold drop-shadow-lg tracking-wide text-center"
          style={{ color: 'var(--color-accent)' }}
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ delay: 0.4, type: "spring", stiffness: 120, damping: 10 }}
        >
          {CONSTANTS.GAME_TITLE}
        </motion.h1>
        <motion.p
          className="text-xl md:text-2xl font-medium text-center mb-2"
          style={{ color: 'var(--color-text)', opacity: 0.8 }}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
        >
          {CONSTANTS.GAME_SUBTITLE}
        </motion.p>
        <motion.div 
          className="mt-4 flex flex-col gap-4 w-full max-w-xs"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8 }}
        >
          <motion.button
            onClick={() => handleSelectMode('vs_cpu')}
            className="w-full px-10 py-4 rounded-xl font-extrabold text-2xl shadow-xl transition-all border-2 hover:scale-105 active:scale-95"
            style={{
              background: 'var(--color-button)',
              color: 'var(--color-button-text)',
              borderColor: 'var(--color-accent)'
            }}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            {CONSTANTS.UI_TEXT.PLAY_VS_CPU_BUTTON}
          </motion.button>
          <motion.button
            onClick={() => handleSelectMode('hotseat')}
            className="w-full px-10 py-4 rounded-xl font-extrabold text-xl shadow-xl transition-all border-2 hover:scale-105 active:scale-95"
            style={{
              background: 'var(--color-secondary)',
              color: 'var(--color-text)',
              borderColor: 'var(--color-accent)'
            }}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            {CONSTANTS.UI_TEXT.PLAY_FRIENDS_BUTTON}
          </motion.button>
        </motion.div>
      </div>
      <div className="absolute bottom-6 text-xs tracking-wide" style={{ color: 'var(--color-text)', opacity: 0.4 }}>
        {CONSTANTS.GAME_FOOTER}
      </div>

      {/* Settings Modal */}
      <AnimatePresence>
        {showSettings && (
          <SettingsModal onClose={handleSettingsClose} audioManager={audioManager} />
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default TitleScreen;
